/**
 * Chat-UI Execution Log Panel
 *
 * Converts the messages the placement module puts in the Activity sidebar
 * (activityMessages, @agion/mission-stream) into AuditEntry[] and renders them
 * using the @agion/ui ExecutionLog components.
 */

import { useMemo, useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Clock } from 'lucide-react';
import { activityMessages, knownForcedReason, toolOutputForDisplay, toolResultFiles, unknownKind, type HITLModality } from '@agion/mission-stream';
import {
  AuditEntryItem,
  StatsSummary,
  TokenBreakdown,
  ErrorSummary,
  Text,
  mergeToolResults,
  mergeDelegationResults,
  filterNoise,
  resolveDelegationState,
  forcedEndingNote,
  searchConfidenceState,
  type AuditEntry,
  type ExecutionStats,
  type TokenUsageStats,
  type OrchestratorDecisionAuditEntry,
  type DelegationAuditEntry,
  type ToolResultAuditEntry,
  type ErrorAuditEntry,
  type NodeCompleteAuditEntry,
} from '@agion/ui';
import type { StreamingChatMessage } from '../../hooks/useStreamingMissionChat';
import { neutralEnding, neutralEndingMessages } from '../../utils/neutral-endings';
import { failedRunText, runEndReading, runEndRecordOf, type RunEndReading } from '../../utils/run-end';
import { auditApi, type MissionAuditEventRow } from '../../services/audit-api';
import { executionsApi } from '../../services/api';
import { combineReopenedRun, convertPersistedAuditRows, finishPersistedTrail, trailHasFailedEnding } from '../../utils/persisted-audit';
import { useBlockedOutcomeLabels } from '../../hooks/useBlockedOutcomeLabels';
import { useForcedEndingLabels } from '../../hooks/useForcedEndingLabels';
import type { HeldSubject } from '../../types/escalations';

// ============================================================================
// Converter: StreamingChatMessage → AuditEntry
// ============================================================================

const NIL_UUID = '00000000-0000-0000-0000-000000000000';

/**
 * Resolve agent display name using the same logic as CustomerMessageRenderer.
 * Handles nil UUIDs, orchestrator fallback, and task-1 convention.
 */
function resolveAgentLabel(
  agentId?: string,
  nodeId?: string,
  dataLabel?: string,
  agentNameMap?: Record<string, string>,
  orchestratorAgentId?: string,
): string | undefined {
  // Use explicit label from the message data if available
  if (dataLabel) return dataLabel;

  const effectiveAgentId = agentId && agentId !== NIL_UUID ? agentId : undefined;

  // Direct lookup by agent ID or node ID
  if (effectiveAgentId && agentNameMap?.[effectiveAgentId]) return agentNameMap[effectiveAgentId];
  if (nodeId && agentNameMap?.[nodeId]) return agentNameMap[nodeId];

  // Orchestrator fallback — when agentId is nil (common for history messages)
  if (orchestratorAgentId && agentNameMap?.[orchestratorAgentId]) {
    return agentNameMap[orchestratorAgentId];
  }

  // task-1 convention (orchestrator node)
  if (agentNameMap?.['task-1']) return agentNameMap['task-1'];

  return undefined;
}

function toAuditEntry(
  msg: StreamingChatMessage,
  agentNameMap?: Record<string, string>,
  participantNames?: Record<string, string>,
  currentUserName?: string,
  orchestratorAgentId?: string,
  runFailed?: (outcome: string | undefined) => string,
  namedSubject?: Required<HeldSubject>,
): AuditEntry | null {
  const d = msg.data;

  // Resolve agent display name with full fallback chain
  const resolvedLabel = resolveAgentLabel(
    msg.agentId,
    msg.nodeId,
    (d.agentLabel as string) ?? (d.agent_label as string) ?? undefined,
    agentNameMap,
    orchestratorAgentId,
  );

  // The author's name from the message, or from participant names by its id.
  // The current user's name only on an answer this chat sent; an answer with
  // no author keeps no name, and the log shows its neutral heading
  // (agion/Agion#2039).
  // In operator mode the held run's end user is named only by the hold read
  // (agion/Agion#3435).
  const userId = (d.userId as string) ?? (d.user_id as string) ?? undefined;
  const resolvedUserName = (namedSubject && userId === namedSubject.userId ? namedSubject.displayName : undefined)
    ?? (d.userName as string)
    ?? (d.user_name as string)
    ?? (userId ? participantNames?.[userId] : undefined)
    ?? (msg.type === 'hitl_response' && d.sentByThisChat === true ? currentUserName : undefined)
    ?? undefined;

  const base = {
    id: msg.id,
    timestamp: msg.timestamp,
    nodeId: msg.nodeId ?? 'unknown',
    agentId: msg.agentId,
    agentLabel: resolvedLabel,
    userName: resolvedUserName,
  };

  switch (msg.type) {
    case 'agent_turn':
      return {
        ...base,
        type: 'turn' as const,
        turnNumber: (d.turnNumber as number) ?? (d.turn_number as number) ?? 0,
        inputTokens: (d.inputTokens as number) ?? (d.input_tokens as number) ?? 0,
        outputTokens: (d.outputTokens as number) ?? (d.output_tokens as number) ?? 0,
        maxOutputTokens: (d.maxOutputTokens as number) ?? (d.max_output_tokens as number) ?? undefined,
        durationMs: (d.durationMs as number) ?? (d.duration_ms as number) ?? 0,
        model: d.model as string | undefined,
        userPrompt: (d.userPrompt as string) ?? (d.user_prompt as string) ?? undefined,
        assistantResponse: (d.assistantResponse as string) ?? (d.assistant_response as string) ?? undefined,
      };

    case 'tool_call':
      return {
        ...base,
        type: 'tool_call' as const,
        toolCallId: (d.toolCallId as string) ?? (d.tool_call_id as string) ?? '',
        toolName: (d.toolName as string) ?? (d.tool_name as string) ?? '',
        toolInput: (d.toolInput as Record<string, unknown>) ?? (d.tool_input as Record<string, unknown>) ?? {},
      };

    case 'tool_result': {
      // The files it made, for the new chat's Activity entry (agion/Agion#2003).
      const outputFiles = toolResultFiles(d);
      return {
        ...base,
        type: 'tool_result' as const,
        toolCallId: (d.toolCallId as string) ?? (d.tool_call_id as string) ?? '',
        toolName: (d.toolName as string) ?? (d.tool_name as string) ?? '',
        success: (d.success as boolean) ?? true,
        // A produced file's download_url is the Edge host's own address; the
        // file card downloads through the proxy (agion/Agion#2003).
        output: toolOutputForDisplay(d.output),
        error: d.error as string | undefined,
        durationMs: (d.durationMs as number) ?? (d.duration_ms as number) ?? 0,
        ...(outputFiles.length > 0 ? { outputFiles } : {}),
      };
    }

    case 'hitl_request':
      return {
        ...base,
        type: 'hitl_request' as const,
        requestId: (d.requestId as string) ?? (d.request_id as string) ?? '',
        modality: (d.modality as HITLModality) ?? 'text',
        prompt: (d.prompt as string) ?? '',
        options: d.options as string[] | undefined,
        pending: (d.pending as boolean) ?? true,
      };

    case 'hitl_response':
      return {
        ...base,
        type: 'hitl_response' as const,
        requestId: (d.requestId as string) ?? (d.request_id as string) ?? '',
        modality: (d.modality as HITLModality) ?? 'text',
        response: (d.response as string) ?? '',
        responseTimeMs: (d.responseTimeMs as number) ?? (d.response_time_ms as number) ?? 0,
      };

    case 'orchestrator_decision':
      return {
        ...base,
        type: 'orchestrator_decision' as const,
        iteration: (d.iteration as number) ?? 0,
        reasoning: (d.reasoning as string) ?? '',
        action: (d.action as 'delegate' | 'synthesize' | 'request_approval' | 'complete') ?? 'delegate',
        targetAgent: (d.targetAgent as string) ?? (d.target_agent as string) ?? undefined,
        model: d.model as string | undefined,
        tokensUsed: (d.tokensUsed as number) ?? (d.tokens_used as number) ?? undefined,
      };

    case 'delegation':
      return {
        ...base,
        type: 'delegation' as const,
        delegationId: (d.delegationId as string) ?? (d.delegation_id as string) ?? '',
        agentNodeId: (d.agentNodeId as string) ?? (d.agent_node_id as string) ?? '',
        agentLabel: (d.agentLabel as string) ?? (d.agent_label as string) ?? '',
        task: (d.task as string) ?? '',
        context: d.context as string | undefined,
        constraints: d.constraints as string | undefined,
        depth: (d.depth as number) ?? 0,
      };

    case 'delegation_result':
      return {
        ...base,
        type: 'delegation_result' as const,
        delegationId: (d.delegationId as string) ?? (d.delegation_id as string) ?? '',
        agentNodeId: (d.agentNodeId as string) ?? (d.agent_node_id as string) ?? '',
        agentLabel: (d.agentLabel as string) ?? (d.agent_label as string) ?? '',
        success: (d.success as boolean) ?? true,
        summary: d.summary as string | undefined,
        error: d.error as string | undefined,
        durationMs: (d.durationMs as number) ?? (d.duration_ms as number) ?? 0,
        tokensUsed: (d.tokensUsed as number) ?? (d.tokens_used as number) ?? undefined,
        outcome: d.outcome as string | undefined,
        blockedReason: (d.blockedReason as string) ?? (d.blocked_reason as string) ?? undefined,
      };

    case 'agent_thinking':
      return {
        ...base,
        type: 'agent_thinking' as const,
        turnNumber: (d.turnNumber as number) ?? (d.turn_number as number) ?? 0,
        content: (d.content as string) ?? '',
        redacted: (d.redacted as boolean) ?? false,
        model: d.model as string | undefined,
      };

    case 'node_status': {
      const status = d.status as string;
      if (status === 'running') {
        return {
          ...base,
          type: 'node_start' as const,
          nodeLabel: (d.nodeLabel as string) ?? (d.node_label as string) ?? undefined,
        };
      }
      if (status === 'completed' || status === 'failed') {
        return {
          ...base,
          type: 'node_complete' as const,
          nodeLabel: (d.nodeLabel as string) ?? (d.node_label as string) ?? undefined,
          durationMs: (d.durationMs as number) ?? (d.duration_ms as number) ?? undefined,
          success: status === 'completed',
          error: d.error as string | undefined,
          outcome: d.outcome as string | undefined,
          blockedReason: (d.blockedReason as string) ?? (d.blocked_reason as string) ?? undefined,
          forcedReason: knownForcedReason(d.forcedReason),
        };
      }
      return null;
    }

    case 'mission_failed':
      // A neutral ending (finished with an answer to check) is a warning,
      // matching the chat's notice; any other failure is an error. The row
      // says why in the words of the run's ending code, never the run's raw
      // error text, which can hold a provider's address (P-095 slice 8).
      return {
        ...base,
        type: 'error' as const,
        message: runFailed ? runFailed(d.outcome as string | undefined) : 'Mission failed',
        details: d.details,
        severity: neutralEnding(d.outcome) ? 'warning' as const : 'error' as const,
      };

    // A completed run has no row; MissionChatWithArtifacts ends the live
    // indicator on it.
    case 'mission_completed':
      return null;

    // Conversation only: activityMessages() never passes them here.
    case 'governance_event':
    case 'mission_trigger':
    case 'transcript':
    case 'system_notification':
    case 'operator_turn':
      return null;

    default:
      return unknownKind(msg.type, unknownKindEntry(base, msg.type));
  }
}

/**
 * The row of a message kind this build does not know (G6, agion/Agion#2003).
 * The design-system AuditEntryItem draws an entry type it has no case for as a
 * generic row that names the type, so the message shows and nothing throws.
 * AuditEntry has no member for it, hence the cast.
 */
function unknownKindEntry(base: Omit<AuditEntry, 'type'>, kind: string): AuditEntry {
  return { ...base, type: kind } as unknown as AuditEntry;
}

/**
 * A warning row in ErrorSummary: an error entry marked severity 'warning', a
 * node the mission setup blocked (agion/Agion#2370), a completed step the
 * runner forced to finish (agion/Agion#2460), or a successful search whose
 * ranking degraded. Mirrors ErrorSummary so the translated header count
 * and the jump highlight match its rows.
 */
function isWarningEntry(e: AuditEntry): boolean {
  if (e.type === 'error') return (e as ErrorAuditEntry).severity === 'warning';
  if (e.type === 'node_complete') {
    const node = e as NodeCompleteAuditEntry;
    return resolveDelegationState(node.success, node.outcome) === 'blocked'
      || forcedEndingNote(node.success, node.forcedReason) !== undefined;
  }
  if (e.type !== 'tool_result') return false;
  const tr = e as ToolResultAuditEntry;
  return tr.success && searchConfidenceState(tr.output, tr.toolName) === 'degraded';
}

function computeStats(entries: AuditEntry[]): ExecutionStats {
  const tokenUsage: TokenUsageStats = {
    totalInputTokens: 0,
    totalOutputTokens: 0,
    totalTokens: 0,
    byModel: {},
  };

  let turnCount = 0;
  let toolCallCount = 0;
  let hitlRequestCount = 0;
  let delegationCount = 0;
  let minTs = Infinity;
  let maxTs = 0;

  for (const e of entries) {
    if (e.timestamp > 0) {
      if (e.timestamp < minTs) minTs = e.timestamp;
      if (e.timestamp > maxTs) maxTs = e.timestamp;
    }
    switch (e.type) {
      case 'turn': {
        turnCount++;
        tokenUsage.totalInputTokens += e.inputTokens;
        tokenUsage.totalOutputTokens += e.outputTokens;
        tokenUsage.totalTokens += e.inputTokens + e.outputTokens;
        if (e.model) {
          const m = tokenUsage.byModel[e.model] ??= { input: 0, output: 0 };
          m.input += e.inputTokens;
          m.output += e.outputTokens;
        }
        break;
      }
      case 'tool_call':
        toolCallCount++;
        break;
      case 'hitl_request':
        hitlRequestCount++;
        break;
      case 'orchestrator_decision': {
        const dec = e as OrchestratorDecisionAuditEntry;
        if (dec.tokensUsed) {
          tokenUsage.totalTokens += dec.tokensUsed;
          tokenUsage.totalOutputTokens += dec.tokensUsed;
          if (dec.model) {
            const m = tokenUsage.byModel[dec.model] ??= { input: 0, output: 0 };
            m.output += dec.tokensUsed;
          }
        }
        break;
      }
      case 'delegation': {
        const del = e as DelegationAuditEntry;
        if (del.resolvedTokensUsed) {
          tokenUsage.totalTokens += del.resolvedTokensUsed;
          tokenUsage.totalOutputTokens += del.resolvedTokensUsed;
        }
        delegationCount++;
        break;
      }
    }
  }

  // Duration = time span from first to last event (not sum of individual latencies)
  const totalDurationMs = minTs < Infinity && maxTs > minTs ? maxTs - minTs : 0;

  return { turnCount, toolCallCount, hitlRequestCount, delegationCount, tokenUsage, totalDurationMs };
}

// ============================================================================
// Component
// ============================================================================

export interface ExecutionLogPanelProps {
  messages: StreamingChatMessage[];
  isExecuting: boolean;
  /** Map from agentId/nodeId → display name */
  agentNameMap?: Record<string, string>;
  /** Map from userId → display name */
  participantNames?: Record<string, string>;
  /** Current user's display name — used only for answers this chat sent (sentByThisChat) */
  currentUserName?: string;
  /** Orchestrator agent ID — used to resolve agent name when agentId is nil UUID */
  orchestratorAgentId?: string;
  /** Execution ID — enables the persisted-audit fallback when no live or
   *  chat-history derived events are available (completed runs, platform
   *  missions). The durable trail is structural/content-free (ADR-055). */
  executionId?: string | null;
  /** Operator mode (agion/Agion#3435): the held run's end user, named only by the hold read. */
  heldSubject?: HeldSubject;
}

/**
 * The conversation's activity messages as audit entries, one per message, in
 * message order and before any merging: a tool result is still its own entry
 * and a delegation's result is not yet folded into the delegation. The
 * Activity sidebar merges them; the new chat's conversation reads its run
 * steps from them (agion/Agion#2827). namedSubject names the held run's end
 * user in operator mode.
 */
export function rawActivityEntries(
  messages: StreamingChatMessage[],
  agentNameMap?: Record<string, string>,
  participantNames?: Record<string, string>,
  currentUserName?: string,
  orchestratorAgentId?: string,
  runFailed?: (outcome: string | undefined) => string,
  namedSubject?: Required<HeldSubject>,
): AuditEntry[] {
  const result: AuditEntry[] = [];
  for (const msg of activityMessages(messages)) {
    const entry = toAuditEntry(msg, agentNameMap, participantNames, currentUserName, orchestratorAgentId, runFailed, namedSubject);
    if (entry) result.push(entry);
  }
  return result;
}

/**
 * Why a reopened run ended, from its end record (GET /v1/executions/{id}):
 * null when the read fails or the server sent no result.
 */
async function readRunEnd(executionId: string): Promise<RunEndReading | null> {
  try {
    return runEndReading(runEndRecordOf(await executionsApi.getExecution(executionId)));
  } catch (err) {
    console.warn('[ExecutionLogPanel] Failed to read the run end record:', err);
    return null;
  }
}

/**
 * The Activity sidebar's data for one conversation: the live or history-derived
 * entries, the persisted trail for a run that is not streaming, the reopened-run
 * merge of both (agion/Agion#2481) and the stats. The current panel and the new
 * chat's sidebar (agion/Agion#2488) both draw from it.
 */
export function useActivityEntries({ messages, isExecuting, agentNameMap, participantNames, currentUserName, orchestratorAgentId, executionId, heldSubject }: ExecutionLogPanelProps): { displayEntries: AuditEntry[]; stats: ExecutionStats } {
  const { t, i18n } = useTranslation(['chat']);
  const [persistedEntries, setPersistedEntries] = useState<AuditEntry[] | null>(null);
  // The ended run's end record, read with its trail: a run that ended on
  // screen words its failed row as the chat's end message does, with what to
  // do next and the stored code (agion/Agion#2947).
  const [runEnd, setRunEnd] = useState<{ executionId: string; value: RunEndReading | null } | null>(null);
  const language = i18n?.language ?? 'en';
  const endOfRun = runEnd && runEnd.executionId === executionId ? runEnd.value : null;

  const subjectId = heldSubject?.userId;
  const subjectName = heldSubject?.displayName;
  const entries = useMemo(() => {
    const namedSubject = subjectId
      ? { userId: subjectId, displayName: subjectName || t('common:unnamedPerson', 'Name not recorded') }
      : undefined;
    const result = rawActivityEntries(messages, agentNameMap, participantNames, currentUserName, orchestratorAgentId, (outcome) => failedRunText(t, endOfRun, outcome, language), namedSubject);

    // Mark HITL requests as no longer pending if a response exists
    const respondedRequestIds = new Set<string>();
    for (const e of result) {
      if (e.type === 'hitl_response') {
        const requestId = (e as import('@agion/ui').HITLResponseAuditEntry).requestId;
        if (requestId) respondedRequestIds.add(requestId);
      }
    }
    for (const e of result) {
      if (e.type === 'hitl_request') {
        const req = e as import('@agion/ui').HITLRequestAuditEntry;
        if (respondedRequestIds.has(req.requestId)) {
          req.pending = false;
        }
      }
    }

    // Merge tool_result into tool_call, delegation_result into delegation, then filter noise
    return filterNoise(mergeDelegationResults(mergeToolResults(result)));
  }, [messages, t, subjectId, subjectName, endOfRun, language]);

  // Clear stale persisted entries when switching executions
  useEffect(() => {
    setPersistedEntries(null);
  }, [executionId]);

  // A run that ends while the panel shows it reads its trail again: a trail
  // read while the run worked holds neither its last turns nor its end
  // (agion/Agion#2871).
  const runEnded = useMemo(
    () => messages.some((m) => m.type === 'mission_completed' || m.type === 'mission_failed'),
    [messages],
  );

  // Persisted-audit trail: load the durable structural record from
  // governance-service for any non-live execution. This is the authoritative,
  // complete trail — it carries agent turns, token counts and model names that
  // the chat-history reconstruction omits (history yields only tool calls and
  // HITL turns, never agent_turn rows). Loaded whenever an executionId exists
  // and we are not actively streaming, regardless of how many chat-history
  // entries were reconstructed, so the stats cards and per-model breakdown
  // populate for completed runs instead of reading zero off a turn-less
  // history projection.
  // A failed ending reads as the live view words it: the run's end record
  // when the server sent one, else the stored code's term, else a plain
  // sentence (agion/Agion#2946).
  useEffect(() => {
    if (!executionId || isExecuting) return;

    let cancelled = false;
    void (async () => {
      let rows: MissionAuditEventRow[];
      try {
        rows = await auditApi.getExecutionAuditEvents(executionId);
      } catch (err) {
        console.warn('[ExecutionLogPanel] Failed to load persisted audit trail:', err);
        return;
      }
      const end = trailHasFailedEnding(rows) ? await readRunEnd(executionId) : null;
      if (cancelled) return;
      setRunEnd({ executionId, value: end });
      setPersistedEntries(
        finishPersistedTrail(
          convertPersistedAuditRows(rows, {
            contentOnHost: t('chat:executionLog.contentOnHost', 'Content stored on the Edge host'),
            mission: t('chat:executionLog.mission', 'Mission'),
            neutralEndings: neutralEndingMessages(t),
            runFailed: (code) => failedRunText(t, end, code, language),
          }),
        ),
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [executionId, isExecuting, runEnded, t, language]);

  // Source selection. Live streaming emits agent_turn events (with tokens and
  // model), so whenever the live/chat-history entries already carry turns they
  // win — never disrupt an active or fully-streamed run. Otherwise (a completed
  // run reopened from history, whose reconstruction has no turns) the durable
  // structural trail supplies the turns, start and end, and the Edge history
  // supplies the tool and question content it holds (agion/Agion#2481). Final
  // fallback is the history entries while the trail loads.
  const displayEntries = useMemo(() => {
    if (entries.some((e) => e.type === 'turn')) return entries;
    if (persistedEntries && persistedEntries.length > 0) return combineReopenedRun(entries, persistedEntries);
    return entries;
  }, [entries, persistedEntries]);

  const stats = useMemo(() => computeStats(displayEntries), [displayEntries]);

  return { displayEntries, stats };
}

export function ExecutionLogPanel(props: ExecutionLogPanelProps) {
  const { isExecuting } = props;
  const { t } = useTranslation(['chat']);
  const timelineEndRef = useRef<HTMLDivElement>(null);
  const { displayEntries, stats } = useActivityEntries(props);

  const rankingDegradedLabel = t('chat:executionLog.rankingDegraded', 'Ranking degraded');
  const noConfidentMatchLabel = t('chat:executionLog.noConfidentMatch', 'No confident match');
  // The end of a run a person stopped (agion/Agion#2947).
  const stoppedLabel = t('chat:nextSections.recent.outcome.cancelled', 'Stopped');
  // Memoised: AuditEntryItem is memo()-wrapped and this panel re-renders per
  // streamed token, so a fresh labels object would re-render every entry.
  const blockedLabels = useBlockedOutcomeLabels();
  const forcedLabels = useForcedEndingLabels();
  const entryLabels = useMemo(
    () => ({ rankingDegraded: rankingDegradedLabel, noConfidentMatch: noConfidentMatchLabel, stopped: stoppedLabel, ...blockedLabels, ...forcedLabels }),
    [rankingDegradedLabel, noConfidentMatchLabel, stoppedLabel, blockedLabels, forcedLabels],
  );

  const warningCount = useMemo(() => displayEntries.filter(isWarningEntry).length, [displayEntries]);

  // Track new entries for stagger animation
  const prevCountRef = useRef(0);
  const newEntryMap = useMemo(() => {
    const map = new Map<string, number>();
    if (displayEntries.length > prevCountRef.current) {
      let idx = 0;
      for (let i = prevCountRef.current; i < displayEntries.length; i++) {
        map.set(displayEntries[i]!.id, idx++);
      }
    }
    return map;
  }, [displayEntries]);

  useEffect(() => {
    prevCountRef.current = displayEntries.length;
  }, [displayEntries.length]);

  // Auto-scroll during execution
  useEffect(() => {
    if (isExecuting && displayEntries.length > 0) {
      timelineEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [displayEntries.length, isExecuting]);

  const scrollToEntry = (entryId: string) => {
    const el = document.getElementById(`audit-entry-${entryId}`);
    if (el) {
      const entry = displayEntries.find((e) => e.id === entryId);
      const ring = entry && isWarningEntry(entry) ? 'ring-amber-400' : 'ring-rose-400';
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('ring-2', ring, 'ring-offset-1');
      setTimeout(() => el.classList.remove('ring-2', ring, 'ring-offset-1'), 1500);
    }
  };

  if (displayEntries.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center h-full">
        <Clock className="h-8 w-8 opacity-20 mb-3" />
        <Text size="sm" color="muted">
          {isExecuting
            ? t('chat:executionLog.waitingForEvents', 'Waiting for events...')
            : t('chat:executionLog.noEventsYet', 'No events yet')}
        </Text>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Stats Summary — white cards with grey border on grey background */}
      <div className="flex-shrink-0 mb-3">
        <StatsSummary
          stats={stats}
          isExecuting={isExecuting}
          cardClassName="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700"
        />
      </div>

      {/* Per-model token breakdown — parity with the mission designer execution
          log. Renders the models used and their input/output token split. */}
      <div className="flex-shrink-0 mb-3">
        <TokenBreakdown
          stats={stats}
          labels={{
            tokenUsage: t('chat:executionLog.tokenUsage', 'Token Usage'),
            in: t('chat:executionLog.in', 'in'),
            out: t('chat:executionLog.out', 'out'),
          }}
        />
      </div>

      {/* Error Summary */}
      <ErrorSummary
        entries={displayEntries}
        onJumpToEntry={scrollToEntry}
        labels={{
          rankingDegraded: rankingDegradedLabel,
          blocked: blockedLabels.blocked,
          forced: forcedLabels.forced,
          warningCount: t('chat:executionLog.warningCount', { count: warningCount, defaultValue: `${warningCount} warning${warningCount === 1 ? '' : 's'}` }),
        }}
      />

      {/* Timeline — flat chronological, with subtle dividers between conversation turns */}
      <div className="flex-1 overflow-y-auto -mx-1 px-1">
        {displayEntries.map((entry, index) => {
          // Insert a subtle divider before hitl_request entries (new conversation turn)
          // but not before the very first entry
          const showTurnDivider = index > 0 && entry.type === 'hitl_request';

          return (
            <div key={entry.id}>
              {showTurnDivider && (
                <div className="my-1.5 border-t border-slate-200/60 dark:border-slate-700/40" />
              )}
              <AuditEntryItem
                entry={entry}
                isLast={index === displayEntries.length - 1}
                isNew={newEntryMap.has(entry.id)}
                staggerIndex={newEntryMap.get(entry.id) ?? 0}
                labels={entryLabels}
              />
            </div>
          );
        })}
        <div ref={timelineEndRef} />
      </div>
    </div>
  );
}
